import React, {
  useState,
} from "react";
import {
  Typography,
  Box,
  Stack,
  IconButton,
  Popover,
  Link,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ACTIVE_COLOR, CHECK_SIZE_LARGE, NOTICE_DELAY_LABEL, NOTICE_DELAY_LABEL_JA, renderStatusCheck, renderStep, StepVariant } from "./NoticeStatusRender";


/* ------------------------------------------------------
 * 通知ステータスの見方（クリックで凡例を表示する）
 * ---------------------------------------------------- */
export const NoticeStatusHelpLink: React.FC = () => {

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // *** アイコンのフロー ***
  const flow: { Icon: typeof TimerOutlinedIcon; label: string; badge?: string }[] = [
    { Icon: TimerOutlinedIcon, label: "送信待ち", badge: NOTICE_DELAY_LABEL },
    { Icon: MailOutlineIcon, label: "メール通知" },
    { Icon: VisibilityOutlinedIcon, label: "アプリでスレッド既読" },
  ];
  // *** チェックマークの凡例 ***
  // 1つの凡例に複数アイコンを並べられるように（送信失敗は既読・未読の両方を表示）
  const checkLegend: { icons: { isConfirmed: boolean; hasError: boolean }[]; label: string }[] = [
    { icons: [{ isConfirmed: true, hasError: false }], label: "アプリでスレッド\n既読" },
    { icons: [{ isConfirmed: false, hasError: false }], label: "アプリでスレッド\n未読" },
    {
      icons: [
        { isConfirmed: true, hasError: true },
        { isConfirmed: false, hasError: true },
      ],
      label: "メール送信失敗",
    },
  ];
  // *** 凡例 ***
  const legend: { variant: StepVariant; label: string }[] = [
    { variant: "active", label: "完了" },
    { variant: "waiting", label: "待機中" },
    { variant: "idle", label: "まだ" },
    { variant: "skipped", label: "スキップ（既読のためメール送信なし）" },
    { variant: "error", label: "送信失敗" },
  ];

  // 区分けの見出し（左に緑のバー）
  const renderSectionTitle = (title: string) => (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        mb: 1,
        pl: 1,
        borderLeft: `3px solid ${ACTIVE_COLOR}`,
        lineHeight: 1.4,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <>
      <Link
        component="button"
        type="button"
        variant="caption"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ m: 1, display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        通知ステータスの見方
      </Link>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: {
            mt: -1.5,   // 通知ステータスの見方のPaperを上方向にずらす（topで位置を決めているため、mbは効かない）
            width: 560  // 通知ステータスの横幅
        } } }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              通知ステータスの見方
            </Typography>
            <IconButton
              onClick={() => setAnchorEl(null)}
              sx={{ ml: "auto", p: 0.5 }}
              aria-label="閉じる"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* 通知先の名前横に表示するチェックマーク */}
          {renderSectionTitle("チェックマーク")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto auto auto",
              columnGap: 3,
              rowGap: 0.75,
              mb: 1,
            }}
          >
            {checkLegend.map(({ icons, label }) => (
              <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
                {icons.map(({ isConfirmed, hasError }, idx) => (
                  <React.Fragment key={idx}>
                    {renderStatusCheck(isConfirmed, hasError, CHECK_SIZE_LARGE)}
                  </React.Fragment>
                ))}
                {/* 改行（\n）を反映し、2行でも収まるよう少し小さめにする */}
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", fontSize: "0.8rem", lineHeight: 1.3 }}
                >
                  {label}
                </Typography>
              </Stack>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            既読は、通知先ユーザがアプリでスレッドを開いたことを表します。<br />
            名前にマウスを乗せると、メール通知の状況を確認できます。
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* マウスオーバー時の詳細 */}
          {renderSectionTitle("詳細の見方")}
          <Typography variant="caption" color="text.secondary">
            左から順に進みます
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ mt: 0.5, mb: 1.5, flexWrap: "wrap" }}
          >
            {flow.map(({ Icon, label, badge }, idx) => (
              <React.Fragment key={label}>
                {idx > 0 && (
                  <Typography variant="body2" color="text.disabled">
                    →
                  </Typography>
                )}
                {renderStep(Icon, "idle", badge)}
                <Typography variant="body2">{label}</Typography>
              </React.Fragment>
            ))}
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            投稿から{NOTICE_DELAY_LABEL_JA}経過してもスレッドが未読の場合に、メールで通知します。<br />
            {NOTICE_DELAY_LABEL_JA}以内にアプリで既読になった場合、メールは送信されません。
          </Typography>
          
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto auto",
              columnGap: 3,
              rowGap: 0.75,
            }}
          >
            {legend.map(({ variant, label }) => (
              <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
                {renderStep(null, variant)}
                <Typography variant="body2">{label}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
